import { useCallback, useEffect, useMemo, useState } from "react";
import logger from "@/lib/logger";
import { useLatestJournal } from "@/components/dashboard/useLatestJournal";
import { cn, formatDate } from "@/lib/utils";
import { useLocalStorage } from "@/components/useLocalStorage";
import {
  createEventSource,
  EventSourceMessage,
  EventSourceMessageJournal
} from "@/components/dashboard/createEventSource";
import { JSONSafe, TaskType } from "@/types";
import { Package, Rocket, SatelliteDish, Server, Text } from "lucide-react";
import { mutate } from "swr";

const hash = (message: EventSourceMessageJournal) => {
  return `${message.createdAt.toISOString()}-${message.message}`;
};

const taskTypeToIcon = (taskType?: TaskType) => {
  switch (taskType) {
    case "build:app":
      return <Package className="w-4 h-4" />;
    case "test":
      return <SatelliteDish className="w-4 h-4" />;
    case "prepare:host":
      return <Server className="w-4 h-4" />;
    case "deploy:app":
      return <Rocket className="w-4 h-4" />;
    default:
      return <Text className="w-4 h-4" />;
  }
};

/**
 * We can't deduplicate by id, because when messages
 * are streamed from the backend, they don't have the same
 * ids as the ones we get from the db.
 */
const deduplicate = (messages: EventSourceMessageJournal[]) => {
  const seen = new Set<string>();
  const result = messages
    .filter(message => {
      if (seen.has(hash(message))) {
        return false;
      } else {
        seen.add(hash(message));
        return true;
      }
    })
    .sort((a, b) => b.createdAt.toISOString().localeCompare(a.createdAt.toISOString()));
  return result;
};

export const JournalDrawer = ({ maxMessages = 30 }: { maxMessages?: number }) => {
  const [open, setOpen] = useLocalStorage<boolean>("journalDrawerOpen", false);
  const [logs, setLogs] = useState<EventSourceMessageJournal[]>([]);
  const { latest } = useLatestJournal(true, undefined, open ? 1000 : undefined, maxMessages);

  useEffect(() => {
    setLogs(logs => deduplicate([...logs, ...latest]).slice(0, maxMessages));
  }, [latest, maxMessages]);

  const consumeMessage = useCallback(
    (message: JSONSafe<EventSourceMessage>) => {
      if (message.type === "invalidate") {
        logger.info("Invalidate API path", message.path);
        mutate(message.path);
        // Also mutate all with filters
        if (message.path.indexOf("?") === -1) {
          mutate(key => typeof key === "string" && key.startsWith(message.path + "?"));
        }
        return;
      }

      const fixed = {
        ...message,
        createdAt: new Date(message.createdAt)
      };

      setLogs(logs => deduplicate([fixed, ...logs]).slice(0, maxMessages));
    },
    [maxMessages]
  );

  useEffect(() => {
    let eventSource: EventSource | undefined;

    const onError = (error: Event) => {
      logger.warn("Event source error", JSON.stringify(error));
      eventSource?.close();
      eventSource = createEventSource(onError);
    };

    eventSource = createEventSource(onError, consumeMessage);

    // On document press ESC key, close the drawer
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => {
      eventSource?.close();
      document.removeEventListener("keydown", handleEsc);
    };
  }, [setOpen, consumeMessage]);

  const lastMessage = useMemo(() => {
    if (
      logs &&
      logs.length > 0 &&
      logs[0]?.message &&
      // eslint-disable-next-line react-hooks/purity
      new Date(logs[0].createdAt).getTime() > Date.now() - 1000 * 60
    ) {
      return logs[0];
    } else {
      return undefined;
    }
  }, [logs]);

  return (
    <>
      {!open && (
        <div
          className="fixed z-50 left-1/2 -translate-x-1/2 h-6 flex items-center rounded-b-md items-top bg-slate-500 cursor-pointer outline-hidden select-none"
          onClick={() => {
            setOpen(true);
          }}
        >
          {lastMessage ? (
            <div className="py-0 mx-1 flex items-center gap-1">
              <span className="text-white">{taskTypeToIcon(lastMessage.taskType)}</span>
              <span className="text-white text-xs bg-slate-500 line-clamp-1">
                {lastMessage.message}
              </span>
            </div>
          ) : null}

          <button
            className={cn(
              "px-2 bg-blue-600 text-white text-sm text-nowrap h-6",
              lastMessage ? "rounded-br-md" : "rounded-b-md"
            )}
            onClick={() => {
              setOpen(true);
            }}
          >
            Open Log
          </button>
        </div>
      )}

      <div
        className={`fixed z-50 top-0 left-0 w-full bg-gray-800 text-white transform ${
          open ? "translate-y-0" : "-translate-y-full"
        } transition-transform duration-300 ease-in-out`}
      >
        <div className="overflow-y-auto p-4 max-h-[400px]">
          {logs.map((log, index) => (
            <div
              key={log.id + "-" + index}
              className="py-1 border-b border-color border-muted-foreground/20 text-sm flex items-center gap-1"
            >
              <span className="opacity-60 min-w-[4.3em] text-right">
                {formatDate(log.createdAt, "HH:mm:ss")}
              </span>{" "}
              {log.taskType ? (
                <span className={cn("opacity-60 text-white")}>
                  {taskTypeToIcon(log.taskType)}
                </span>
              ) : null}
              <span className="line-clamp-1">{log.message}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-center">
          <button
            className="px-2 py-1 bg-blue-600 text-white rounded-t-md text-sm"
            onClick={() => setOpen(!open)}
          >
            Close Log
          </button>
        </div>
      </div>
    </>
  );
};
